import type { MouseEventHandler } from 'react';
import type { CalculatedAttributeResult } from '../../calculatedAttributes/resolve';
import type { BaseAttribute, SystemAttribute } from '../../types/attributes';
import type { CmsProduct } from '../../types/cmsProduct';
import {
  formatAttributeItemValue,
  resolveProductTag,
} from '../../utils/helpers';
import {
  currencyFormat,
  formatNumberWithSeparator,
} from '../../utils/helpers/formatters';
import { currencyFormatterNoDecimal } from '../../utils/number';
import {
  findHighlightedProductAttribute,
  resolveHighlightAttributeName,
} from './highlightAttribute';
import type {
  ProductGridCardProps,
  ProductGridCardPreviewItem,
  ProductGridCardPreviewLayout,
} from './types';

export const defaultPreviewLayout: ProductGridCardPreviewLayout = {
  containerWidth: 0,
  itemWidth: 0,
  counterWidth: 0,
  gap: 0,
};

export const getVisiblePreviewCount = ({
  previewCount,
  previewLayout,
}: {
  previewCount: number;
  previewLayout: ProductGridCardPreviewLayout;
}) => {
  const { containerWidth, itemWidth, counterWidth, gap } = previewLayout;

  if (
    previewCount <= 4 ||
    containerWidth <= 0 ||
    itemWidth <= 0 ||
    counterWidth <= 0
  ) {
    return Math.min(previewCount, 4);
  }

  const slotSize = itemWidth + gap;
  const counterSlotSize = counterWidth + gap;
  const maxSlots = Math.floor((containerWidth + gap) / slotSize);

  if (previewCount <= maxSlots) {
    return previewCount;
  }

  const maxItemsWithCounter = Math.floor(
    (containerWidth + gap - counterSlotSize) / slotSize,
  );

  return Math.max(Math.min(maxItemsWithCounter, previewCount), 1);
};

export const hasRenderableCta = (
  label?: string,
  href?: string,
  onClick?: MouseEventHandler<HTMLElement>,
) => Boolean(label && (href || onClick));

const getHighlightMetricPrefix = (
  attribute: BaseAttribute,
): string | undefined => {
  if (
    attribute.__typename !== 'ComponentAttributesAttrNumber' &&
    attribute.__typename !== 'ComponentAttributesAttrNumberRange'
  ) {
    return undefined;
  }

  return attribute.prefix?.trim() || undefined;
};

const getHighlightMetricSuffix = (
  attribute: BaseAttribute,
): string | undefined => {
  if (
    attribute.__typename !== 'ComponentAttributesAttrNumber' &&
    attribute.__typename !== 'ComponentAttributesAttrNumberRange'
  ) {
    return undefined;
  }

  return attribute.suffix?.trim() || undefined;
};

const formatHighlightMetricNumber = (value?: string) => {
  if (!value) {
    return undefined;
  }

  return formatNumberWithSeparator({
    value,
    thousandsSeparator: ',',
  });
};

const getHighlightMetricValue = (
  attribute: BaseAttribute,
): string | undefined => {
  if (
    attribute.__typename !== 'ComponentAttributesAttrNumber' &&
    attribute.__typename !== 'ComponentAttributesAttrNumberRange'
  ) {
    return formatAttributeItemValue(attribute) ?? undefined;
  }

  if (attribute.__typename === 'ComponentAttributesAttrNumber') {
    if (!attribute.value) {
      return attribute.empty_value_text?.trim() || undefined;
    }

    return formatHighlightMetricNumber(attribute.value);
  }

  if (!attribute.value_start) {
    return undefined;
  }

  const valueStart = formatHighlightMetricNumber(attribute.value_start);
  const valueEnd = formatHighlightMetricNumber(attribute.value_end);

  if (!valueEnd) {
    return valueStart;
  }

  return `${valueStart} - ${valueEnd}`;
};

export const getProductGridCardMetrics = ({
  product,
  highlightTextAttribute,
  highlightTextOverride,
  resolveCalculated,
}: {
  product?: CmsProduct;
  highlightTextAttribute?: SystemAttribute;
  highlightTextOverride?: string;
  /**
   * The engine facade, threaded in because this runs inside a map and cannot
   * call a hook. Absent outside a CMS page — the metric then reads the
   * catalogue, as it always did (CMS-1890).
   */
  resolveCalculated?: (
    attributeName: string | undefined,
    product: CmsProduct | undefined,
  ) => CalculatedAttributeResult | undefined;
}): Pick<
  ProductGridCardProps,
  'metricPrefix' | 'metricPrimary' | 'metricSecondary'
> => {
  if (highlightTextOverride?.trim()) {
    return {
      metricPrefix: undefined,
      metricPrimary: highlightTextOverride.trim(),
      metricSecondary: undefined,
    };
  }

  const matchedAttribute = findHighlightedProductAttribute(
    highlightTextAttribute,
    product?.attributes,
  );

  // The engine is asked BEFORE the catalogue match is required: it reads the
  // attribute only for a prefix and suffix, so a product carrying no shell for
  // the highlighted attribute still gets its figure. Asking afterwards left
  // exactly those cards blank — the CMS-1786 symptom this is meant to fix. The
  // name comes from the shared resolver, so the collector asks for exactly the
  // card this renders.
  const calculated = resolveCalculated?.(
    resolveHighlightAttributeName(highlightTextAttribute, product),
    product,
  );
  if (calculated) {
    return {
      metricPrefix: undefined,
      metricPrimary:
        calculated.state === 'resolved'
          ? calculated.value.formatted
          : undefined,
      metricSecondary: undefined,
    };
  }

  if (!matchedAttribute) {
    return {
      metricPrefix: undefined,
      metricPrimary: undefined,
      metricSecondary: undefined,
    };
  }

  return {
    metricPrefix: getHighlightMetricPrefix(matchedAttribute),
    metricPrimary: getHighlightMetricValue(matchedAttribute),
    metricSecondary: getHighlightMetricSuffix(matchedAttribute),
  };
};

export const getProductGridCardPreviewItems = ({
  product,
  orderByOffer = false,
}: {
  product?: CmsProduct;
  // When true (Reward Toggle / CMS-1857), keep the Exclusive → Bundle-Exclusive → Welcome offer
  // order within the product and use the first item as the primary reward. When false (default,
  // e.g. the reward preview icon row), keep the legacy main-gift-then-value sort.
  orderByOffer?: boolean;
}): ProductGridCardPreviewItem[] => {
  const gifts = [
    ...(product?.exclusive_offers ?? []),
    ...(product?.bundle_exclusive_offers ?? []),
    ...(product?.welcome_offers ?? []),
  ]
    .flatMap((offer) => offer.list_of_gifts ?? [])
    .filter((gift) => gift.gift);

  const orderedGifts = orderByOffer
    ? gifts
    : [...gifts].sort((left, right) => {
        if (left.main_gift !== right.main_gift) {
          return left.main_gift ? -1 : 1;
        }

        const rightPrice =
          right.gift?.gift_value?.price ??
          right.gift?.gift_value?.topup_amount ??
          0;
        const leftPrice =
          left.gift?.gift_value?.price ??
          left.gift?.gift_value?.topup_amount ??
          0;

        return rightPrice - leftPrice;
      });

  return orderedGifts.reduce<ProductGridCardPreviewItem[]>((acc, gift) => {
    const imageUrl = gift.gift?.mobile_image || gift.gift?.desktop_image;
    const title = gift.gift?.gift_title || undefined;

    if (!imageUrl && !title) {
      return acc;
    }

    if (
      acc.some((item) => item.imageUrl === imageUrl && item.title === title)
    ) {
      return acc;
    }

    const giftValue = gift.gift?.gift_value;
    const worthValueText =
      giftValue?.price && giftValue.prefix
        ? `${currencyFormat(giftValue.prefix)}${currencyFormatterNoDecimal.format(giftValue.price)}`
        : undefined;
    const topupAmount = giftValue?.topup_amount ?? 0;
    const topupText =
      topupAmount > 0 && giftValue?.prefix
        ? `+${currencyFormat(giftValue.prefix)}${currencyFormatterNoDecimal.format(topupAmount)}`
        : undefined;

    acc.push({
      imageUrl,
      title,
      worthValueText,
      topupText,
    });

    return acc;
  }, []);
};

const normalizeUrl = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return url.startsWith('/') ? url : `/${url}`;
};

export const mapProductToProductGridCardProps = ({
  product,
  primaryCtaLabel,
  primaryCtaUrl,
  onPrimaryCtaClick,
  secondaryCtaLabel,
  secondaryCtaUrl,
  onSecondaryCtaClick,
}: {
  product: CmsProduct;
  primaryCtaLabel?: string;
  primaryCtaUrl?: string;
  onPrimaryCtaClick?: ProductGridCardProps['onPrimaryCtaClick'];
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
  onSecondaryCtaClick?: ProductGridCardProps['onSecondaryCtaClick'];
}): ProductGridCardProps => {
  const viewDetailsUrl = normalizeUrl(secondaryCtaUrl);
  const applyUrl = normalizeUrl(primaryCtaUrl);

  return {
    id: product.entryUID ?? product.uid ?? product.title,
    tagText: resolveProductTag({ product }),
    imageUrl: product.product_image || product.provider?.provider_image,
    imageAlt: product.product_name || 'Product image',
    title: product.product_name,
    previewItems: getProductGridCardPreviewItems({
      product,
    }),
    previewStyle: 'icon',
    primaryCtaLabel,
    primaryCtaUrl: applyUrl,
    onPrimaryCtaClick,
    primaryCtaPosition: 'afterImage',
    primaryCtaIcon: 'arrow',
    secondaryCtaLabel:
      viewDetailsUrl || onSecondaryCtaClick ? secondaryCtaLabel : undefined,
    secondaryCtaUrl: viewDetailsUrl,
    onSecondaryCtaClick,
  };
};
